import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useGet, api } from "../../api";
import {
  IKindredDealsResponse,
  IKindredBrandReference,
  KindredPublishStatus,
} from "../../interfaces";
import {
  Button,
  Spinner,
  Form,
  Col,
  Badge,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { Link } from "react-router";
import Select from "react-select";
import {
  listSearchKey,
  settingsType,
  useListSearchMemory,
  useSettings,
} from "../../hooks";
import { useAuth } from "../../hooks/auth";
import { Copy, Check, Code, Plus } from "react-feather";
import { BrandMultiSelect } from "../../components/workbench";
import { FilterBar, FilterField, PageHeader } from "../../components/ui";

const publishedStatusOptions = [
  { value: "", label: "All" },
  { value: "0", label: "Draft" },
  { value: "1", label: "Awaiting Approval" },
  { value: "2", label: "Approved" },
  { value: "3", label: "Deactivated" },
];

const suspendedOptions = [
  { value: "", label: "All" },
  { value: "true", label: "Suspended" },
  { value: "false", label: "Not Suspended" },
];

/** What each status actually means — asked often enough to belong on the page. */
const STATUS_MEANING: Record<number, string> = {
  [KindredPublishStatus.Draft]:
    "Draft — created but never submitted for approval. Not live to shoppers.",
  [KindredPublishStatus.AwaitingApproval]:
    "Awaiting Approval — submitted and waiting on a reviewer. Not live to shoppers.",
  [KindredPublishStatus.Approved]:
    "Approved — live to shoppers, provided it is in date and not suspended.",
  [KindredPublishStatus.Deactivated]:
    "Deactivated — switched off after being live. Not live to shoppers.",
};

const PUBLISHED_STATUS_HINT =
  "Draft = never submitted for approval. Deactivated = switched off after going live. Neither is visible to shoppers.";

const EXCLUSIVE_HINT =
  "Exclusive means this deal's commercial terms are only available through Kindred — it is a campaign-level flag, not a coupon code.";

const statusBadge = (status: number) => {
  const map: Record<number, { bg: string; label: string }> = {
    [KindredPublishStatus.Draft]: { bg: "secondary", label: "Draft" },
    [KindredPublishStatus.AwaitingApproval]: {
      bg: "warning",
      label: "Awaiting",
    },
    [KindredPublishStatus.Approved]: { bg: "success", label: "Approved" },
    [KindredPublishStatus.Deactivated]: { bg: "danger", label: "Deactivated" },
  };
  const info = map[status] || { bg: "secondary", label: String(status) };
  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip>{STATUS_MEANING[status] || `Status ${status}`}</Tooltip>
      }
    >
      <Badge bg={info.bg}>{info.label}</Badge>
    </OverlayTrigger>
  );
};

const formatDate = (d: string | null) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

const CopyButton: React.FC<{ text: string; title?: string }> = ({
  text,
  title,
}) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{copied ? "Copied!" : title || "Copy"}</Tooltip>}
    >
      <button
        type="button"
        className="btn btn-link btn-sm p-0 ms-1"
        style={{ lineHeight: 1, verticalAlign: "middle" }}
        onClick={handleCopy}
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
      </button>
    </OverlayTrigger>
  );
};

export const KindredDeals: React.FC = () => {
  const { hasPreviousSearch, initialPage, rememberSearch, forgetSearch } =
    useListSearchMemory(listSearchKey.KINDRED_DEALS);
  const [page, setPage] = useState(initialPage);
  const [limit] = useState(100);

  // Filters (persisted)
  const [publishedStatus, setPublishedStatus] = useSettings(
    settingsType.KINDRED_DEALS_PUBLISHED_STATUS
  );
  const [suspended, setSuspended] = useSettings(
    settingsType.KINDRED_DEALS_SUSPENDED
  );
  const [source, setSource] = useSettings(settingsType.KINDRED_DEALS_SOURCE);
  const [countryCode, setCountryCode] = useSettings(
    settingsType.KINDRED_DEALS_COUNTRY
  );
  const [cashbackType, setCashbackType] = useSettings(
    settingsType.KINDRED_DEALS_CASHBACK_TYPE
  );
  const [domain, setDomain] = useSettings(settingsType.KINDRED_DEALS_DOMAIN);
  const [featured, setFeatured] = useSettings(
    settingsType.KINDRED_DEALS_FEATURED
  );
  const [topBrands, setTopBrands] = useSettings(
    settingsType.KINDRED_DEALS_TOP_BRANDS
  );
  const [brandId, setBrandId] = useSettings(
    settingsType.KINDRED_DEALS_BRAND_ID
  );
  const [campaignId, setCampaignId] = useSettings(
    settingsType.KINDRED_DEALS_CAMPAIGN_ID
  );
  const [merchantId, setMerchantId] = useSettings(
    settingsType.KINDRED_DEALS_MERCHANT_ID
  );
  const [keywords, setKeywords] = useSettings(
    settingsType.KINDRED_DEALS_KEYWORDS
  );
  const [activeDomainsOnly, setActiveDomainsOnly] = useSettings(
    settingsType.KINDRED_DEALS_ACTIVE_DOMAINS_ONLY
  );
  const [active, setActive] = useSettings(settingsType.KINDRED_DEALS_ACTIVE);

  const [forceRefresh, setForceRefresh] = useState(false);

  // Auth for direct API calls
  const { getToken } = useAuth();

  // Reference data (countries & networks only — brands use async search)
  const refCountries = useGet<string[]>("kindred/reference/countries", true);
  const refNetworks = useGet<string[]>("kindred/reference/networks", true);

  // Brand async search for typeahead
  const [selectedBrands, setSelectedBrands] = useState<
    readonly { value: string; label: string }[]
  >([]);

  // Resolve stored brandIds on mount
  useEffect(() => {
    if (brandId && selectedBrands.length === 0) {
      (async () => {
        try {
          const token = await getToken();
          const ids = brandId.split(",").filter(Boolean);
          const resolved = await Promise.all(
            ids.map(async (id: string) => {
              try {
                const brand = await api.get<IKindredBrandReference>(
                  `kindred/reference/brands/${encodeURIComponent(id)}`,
                  {
                    headers: {
                      Authorization: token ? `Bearer ${token}` : undefined,
                    },
                  }
                );
                return brand
                  ? {
                      value: brand.id,
                      label: `${brand.name}${brand.isTopBrand ? " ★" : ""}`,
                    }
                  : null;
              } catch {
                return null;
              }
            })
          );
          setSelectedBrands(
            resolved.filter(Boolean) as { value: string; label: string }[]
          );
        } catch {
          // brands not found — clear
        }
      })();
    }
  }, []);

  // Build query
  let queryParams = `page=${page}&limit=${limit}`;
  if (publishedStatus) queryParams += `&publishedStatus=${publishedStatus}`;
  if (suspended) queryParams += `&suspended=${suspended}`;
  if (source) queryParams += `&source=${encodeURIComponent(source)}`;
  if (countryCode)
    queryParams += `&countryCode=${encodeURIComponent(countryCode)}`;
  if (cashbackType)
    queryParams += `&cashbackType=${encodeURIComponent(cashbackType)}`;
  if (domain) queryParams += `&domain=${encodeURIComponent(domain)}`;
  if (featured) queryParams += `&featured=true`;
  if (topBrands) queryParams += `&topBrands=true`;
  if (brandId) queryParams += `&brandId=${encodeURIComponent(brandId)}`;
  if (campaignId)
    queryParams += `&campaignId=${encodeURIComponent(campaignId)}`;
  if (merchantId)
    queryParams += `&merchantId=${encodeURIComponent(merchantId.trim())}`;
  if (keywords) queryParams += `&keywords=${encodeURIComponent(keywords)}`;
  if (activeDomainsOnly) queryParams += `&activeDomainsOnly=true`;
  if (active) queryParams += `&active=true`;
  if (forceRefresh) queryParams += `&forceRefresh=true`;

  const results = useGet<IKindredDealsResponse>(
    `kindred/deals?${queryParams}`,
    false
  );

  const handleRefresh = useCallback(async () => {
    rememberSearch(page);
    await results.refetch();
  }, [results, rememberSearch, page]);

  // Re-run the last search on mount so returning from a detail page
  // restores the results, not just the filters.
  const { refetch } = results;
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || !hasPreviousSearch) return;
    restoredRef.current = true;
    refetch().catch(() => undefined);
  }, [hasPreviousSearch, refetch]);

  // Force Refresh has to go through a render: the flag belongs in the query
  // string, and refetch is bound to the URL the hook was last given.
  useEffect(() => {
    if (!forceRefresh) return;
    handleRefresh()
      .catch(() => undefined)
      .finally(() => setForceRefresh(false));
  }, [forceRefresh]);

  // Only a real page change re-runs the search — the mount render and the
  // reset done by Clear Filters already match what was fetched.
  const lastFetchedPageRef = useRef(initialPage);
  useEffect(() => {
    if (lastFetchedPageRef.current === page) return;
    lastFetchedPageRef.current = page;
    handleRefresh();
  }, [page]);

  const handleClearFilters = () => {
    setPublishedStatus("");
    setSuspended("");
    setSource("");
    setCountryCode("");
    setCashbackType("");
    setDomain("");
    setFeatured(false);
    setTopBrands(false);
    setBrandId("");
    setSelectedBrands([]);
    setCampaignId("");
    setMerchantId("");
    setKeywords("");
    setActiveDomainsOnly(false);
    setActive(false);
    forgetSearch();
    lastFetchedPageRef.current = 1;
    setPage(1);
  };

  const activeCount = useMemo(
    () =>
      [
        publishedStatus,
        suspended,
        source,
        countryCode,
        cashbackType,
        domain,
        brandId,
        campaignId,
        merchantId,
        keywords,
        featured,
        topBrands,
        activeDomainsOnly,
        active,
      ].filter(Boolean).length,
    [
      publishedStatus,
      suspended,
      source,
      countryCode,
      cashbackType,
      domain,
      brandId,
      campaignId,
      merchantId,
      keywords,
      featured,
      topBrands,
      activeDomainsOnly,
      active,
    ]
  );

  const pagination = results.data?.pagination;

  // A new search starts at page 1. Off page 1 the page effect does the fetch,
  // so handleRefresh isn't called with the old page still in its closure.
  const runSearch = () => {
    if (page !== 1) setPage(1);
    else handleRefresh();
  };

  return (
    <div>
      <PageHeader
        title="Kindred Live — Deals"
        description="Campaigns in Kindred Live — brand, merchant ID, terms, countries and domains."
        actions={
          <Link to="/kindred/deals/new">
            <Button size="sm" variant="success">
              <Plus size={14} className="me-1" /> Create Deal
            </Button>
          </Link>
        }
      />

      <FilterBar
        activeCount={activeCount}
        onSearch={runSearch}
        onClear={handleClearFilters}
        summary={
          pagination
            ? `${pagination.total.toLocaleString()} deals · page ${pagination.page} of ${pagination.totalPages}`
            : undefined
        }
        trailing={
          <Button
            size="sm"
            variant="outline-primary"
            title="Bypass the cache and re-read the deals straight from Kindred Live"
            disabled={results.loading}
            onClick={() => setForceRefresh(true)}
          >
            {results.loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Force Refresh"
            )}
          </Button>
        }
      >
        <FilterField
          label="Published Status"
          tooltip={PUBLISHED_STATUS_HINT}
          md={3}
        >
          <Form.Select
            size="sm"
            value={publishedStatus}
            onChange={(e) => setPublishedStatus(e.target.value)}
          >
            {publishedStatusOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </FilterField>

        <FilterField
          label="Suspended"
          tooltip="Switched off by ops without changing the published status."
          md={3}
        >
          <Form.Select
            size="sm"
            value={suspended}
            onChange={(e) => setSuspended(e.target.value)}
          >
            {suspendedOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </FilterField>

        <FilterField label="Source / Network" md={3}>
          <Select
            isMulti
            options={(refNetworks.data || []).map((n) => ({
              value: n,
              label: n,
            }))}
            value={
              source
                ? source.split(",").map((s: string) => ({ value: s, label: s }))
                : []
            }
            onChange={(opts) =>
              setSource((opts || []).map((o) => o.value).join(","))
            }
            placeholder="All networks..."
            styles={{
              control: (base) => ({
                ...base,
                minHeight: 31,
                fontSize: "0.875rem",
              }),
            }}
          />
        </FilterField>

        <FilterField label="Country" md={3}>
          <Select
            isMulti
            options={(refCountries.data || []).map((c) => ({
              value: c,
              label: c,
            }))}
            value={
              countryCode
                ? countryCode
                    .split(",")
                    .map((c: string) => ({ value: c, label: c }))
                : []
            }
            onChange={(opts) =>
              setCountryCode((opts || []).map((o) => o.value).join(","))
            }
            placeholder="All countries..."
            styles={{
              control: (base) => ({
                ...base,
                minHeight: 31,
                fontSize: "0.875rem",
              }),
            }}
          />
        </FilterField>

        <FilterField
          label="Brand"
          tooltip="Type a brand name, or paste a brand ID to jump straight to it."
          md={3}
        >
          <BrandMultiSelect
            value={selectedBrands}
            onChange={(opts) => {
              setBrandId(opts.map((o) => o.value).join(","));
              setSelectedBrands(opts);
            }}
          />
        </FilterField>

        <FilterField
          label="Merchant ID"
          tooltip="The network's own advertiser/merchant ID. Matches any part of it."
          md={3}
        >
          <Form.Control
            size="sm"
            placeholder="e.g. 12345"
            value={merchantId}
            onChange={(e) => setMerchantId(e.target.value)}
          />
        </FilterField>

        <FilterField label="Campaign ID" tooltip="Exact match." md={3}>
          <Form.Control
            size="sm"
            placeholder="Campaign ID"
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
          />
        </FilterField>

        <FilterField label="Cashback Type" md={3}>
          <Form.Control
            size="sm"
            placeholder="e.g. percentage"
            value={cashbackType}
            onChange={(e) => setCashbackType(e.target.value)}
          />
        </FilterField>

        <FilterField label="Domain" md={3}>
          <Form.Control
            size="sm"
            placeholder="Search domains..."
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
          />
        </FilterField>

        <FilterField
          label="Keywords (domains)"
          tooltip="Second free-text pass over the same domain list."
          md={3}
        >
          <Form.Control
            size="sm"
            placeholder="Search keywords in domains..."
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
          />
        </FilterField>

        <Col md={6}>
          <Form.Label className="small fw-semibold mb-1 d-block">
            Flags
          </Form.Label>
          <Form.Check
            inline
            type="checkbox"
            id="deals-featured"
            label="Featured"
            title="Only deals carrying the Featured Level 2 flag."
            checked={featured}
            onChange={(e) => setFeatured(e.target.checked)}
          />
          <Form.Check
            inline
            type="checkbox"
            id="deals-top-brands"
            label="Top Brands"
            title="Only deals whose brand is listed as a Top Brand."
            checked={topBrands}
            onChange={(e) => setTopBrands(e.target.checked)}
          />
          <Form.Check
            inline
            type="checkbox"
            id="deals-active"
            label="Active Only"
            title="In date, not suspended and not deactivated."
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          <Form.Check
            inline
            type="checkbox"
            id="deals-validated-domains"
            label="Validated Domains Only"
            title="Applies to the Domain search above — only match domains marked validated."
            checked={activeDomainsOnly}
            onChange={(e) => setActiveDomainsOnly(e.target.checked)}
          />
        </Col>
      </FilterBar>

      {/* Results */}
      {results.error && (
        <div className="text-danger mb-3">Error: {results.error.message}</div>
      )}

      <div className="list-group list-group-flush">
        {results.loading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" /> Loading...
          </div>
        )}
        {!results.loading && results.data?.results.length === 0 && (
          <div className="text-center text-muted py-3">
            No results. Apply filters and click Search.
          </div>
        )}
        {(results.data?.results || []).map((deal) => (
          <div
            key={deal.id}
            className="list-group-item px-3 py-2"
            style={{ fontSize: "0.85rem" }}
          >
            {/* Row 1: primary info */}
            <div className="d-flex align-items-center flex-wrap gap-2">
              <Link
                to={`/kindred/deals/${deal.id}`}
                className="fw-semibold text-nowrap"
              >
                {deal.id.substring(0, 8)}…
              </Link>
              <CopyButton text={deal.id} title="Copy Deal ID" />
              <span className="text-muted">|</span>
              <span title={`Brand ID: ${deal.brandId}`}>
                {deal.brandName}
                {deal.isTopBrand && (
                  <Badge
                    bg="info"
                    className="ms-1"
                    style={{ fontSize: "0.65rem" }}
                  >
                    ★
                  </Badge>
                )}
              </span>
              <CopyButton text={deal.brandId} title="Copy Brand ID" />
              <span className="text-muted">|</span>
              {statusBadge(deal.publishedStatus)}
              {deal.suspended && (
                <Badge bg="danger" style={{ fontSize: "0.65rem" }}>
                  Suspended
                </Badge>
              )}
              {deal.featuredLevel2 ? (
                <Badge bg="warning" style={{ fontSize: "0.65rem" }}>
                  Featured Lvl 2
                </Badge>
              ) : deal.featured ? (
                <Badge bg="info" style={{ fontSize: "0.65rem" }}>
                  Featured
                </Badge>
              ) : null}
              {deal.exclusive && (
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>{EXCLUSIVE_HINT}</Tooltip>}
                >
                  <Badge bg="dark" style={{ fontSize: "0.65rem" }}>
                    Exclusive
                  </Badge>
                </OverlayTrigger>
              )}
              <span className="ms-auto d-flex align-items-center gap-2 text-nowrap">
                <small className="text-muted">
                  {deal.source} · {deal.cashbackType || "—"} ·{" "}
                  {deal.totalCommission != null
                    ? `${deal.totalCommission}%`
                    : "—"}
                </small>
                <small className="text-muted">
                  {formatDate(deal.startDate)} → {formatDate(deal.endDate)}
                </small>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Copy JSON</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    style={{ lineHeight: 1 }}
                    onClick={() =>
                      navigator.clipboard.writeText(
                        JSON.stringify(deal, null, 2)
                      )
                    }
                  >
                    <Code size={13} />
                  </button>
                </OverlayTrigger>
              </span>
            </div>
            {/* Row 2: merchant, countries & domains */}
            <div
              className="d-flex flex-wrap gap-3 mt-1"
              style={{ fontSize: "0.78rem" }}
            >
              <span>
                <span className="text-muted fw-semibold">Merchant ID: </span>
                {deal.merchantId ? (
                  <>
                    <span>{deal.merchantId}</span>
                    <CopyButton
                      text={deal.merchantId}
                      title="Copy Merchant ID"
                    />
                  </>
                ) : (
                  <span className="text-muted" title="No merchant ID on this campaign">
                    —
                  </span>
                )}
              </span>
              <span>
                <span className="text-muted fw-semibold">Countries: </span>
                {deal.countries.length > 0 ? (
                  <span title={deal.countries.join(", ")}>
                    {deal.countries.length > 10
                      ? `${deal.countries.slice(0, 10).join(", ")}… (${deal.countries.length})`
                      : deal.countries.join(", ")}
                  </span>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </span>
              <span>
                <span className="text-muted fw-semibold">Domains: </span>
                {deal.domains.length > 0 ? (
                  <span title={deal.domains.join(", ")}>
                    {deal.domains.length > 5
                      ? `${deal.domains.slice(0, 5).join(", ")}… (${deal.domains.length})`
                      : deal.domains.join(", ")}
                  </span>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="small text-muted">
            Showing page {pagination.page} of {pagination.totalPages} (
            {pagination.total} total)
          </span>
          <div>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page === 1}
              onClick={() => setPage(1)}
            >
              First
            </Button>{" "}
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>{" "}
            <Form.Select
              size="sm"
              className="d-inline-block w-auto"
              value={page}
              onChange={(e) => setPage(Number(e.target.value))}
            >
              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </Form.Select>{" "}
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page === pagination.totalPages}
              onClick={() =>
                setPage((p) => Math.min(pagination.totalPages, p + 1))
              }
            >
              Next
            </Button>{" "}
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page === pagination.totalPages}
              onClick={() => setPage(pagination.totalPages)}
            >
              Last
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
